import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { OrganizationSettingsResponse, TestMailResult } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { EMPTY_MAIL_FIELDS, MailConnectionFields, type MailFieldsState } from '../components/MailConnectionFields';
import { OrgTabs } from '../components/OrgTabs';
import { useAuth } from '../lib/auth';

const SOURCE_LABEL: Record<TestMailResult['source'], string> = {
  organization: 'eigene Konfiguration dieses Vereins',
  platform: 'globale Konfiguration',
  env: 'Server-Umgebungsvariablen (noch nirgends gespeichert)',
};

export function OrgMailPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const readOnly = !can('organization.edit');
  const { data, isLoading } = useQuery({
    queryKey: ['org', 'settings'],
    queryFn: () => api<OrganizationSettingsResponse>('/org/settings', { orgScoped: true }),
  });

  const [mode, setMode] = useState<'global' | 'custom'>('global');
  const [fields, setFields] = useState<MailFieldsState>(EMPTY_MAIL_FIELDS);
  useEffect(() => {
    if (!data) return;
    setMode(data.mail.mode);
    setFields({
      transport: data.mail.custom.transport,
      host: data.mail.custom.host ?? '',
      port: data.mail.custom.port,
      secure: data.mail.custom.secure,
      user: data.mail.custom.user ?? '',
      password: '',
      apiUrl: data.mail.custom.apiUrl ?? '',
      apiKey: '',
      tenantId: data.mail.custom.tenantId ?? '',
      clientId: data.mail.custom.clientId ?? '',
      clientSecret: '',
      clientSecretExpiresAt: data.mail.custom.clientSecretExpiresAt ?? '',
      graphSaveToSentItems: data.mail.custom.graphSaveToSentItems,
      fromAddress: data.mail.custom.fromAddress ?? '',
      fromName: data.mail.custom.fromName ?? '',
    });
  }, [data]);

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api<OrganizationSettingsResponse>('/org/settings', {
        method: 'PATCH',
        orgScoped: true,
        body: {
          mail: {
            mode,
            custom: {
              transport: fields.transport,
              host: fields.host || undefined,
              port: fields.port,
              secure: fields.secure,
              user: fields.user || undefined,
              password: fields.password || undefined,
              apiUrl: fields.apiUrl || undefined,
              apiKey: fields.apiKey || undefined,
              tenantId: fields.tenantId || undefined,
              clientId: fields.clientId || undefined,
              clientSecret: fields.clientSecret || undefined,
              clientSecretExpiresAt: fields.clientSecretExpiresAt || undefined,
              graphSaveToSentItems: fields.graphSaveToSentItems,
              fromAddress: fields.fromAddress || undefined,
              fromName: fields.fromName || undefined,
            },
          },
        },
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setFields((f) => ({ ...f, password: '', apiKey: '', clientSecret: '' }));
      setTimeout(() => setSaved(false), 2000);
      void qc.invalidateQueries({ queryKey: ['org', 'settings'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<TestMailResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const sendTest = useMutation({
    mutationFn: () =>
      api<TestMailResult>('/org/settings/mail/test', { method: 'POST', orgScoped: true, body: { to: testTo } }),
    onSuccess: (r) => {
      setTestError(null);
      setTestResult(r);
    },
    onError: (e) => {
      setTestResult(null);
      setTestError(e instanceof ApiError ? e.message : 'Testmail konnte nicht verschickt werden.');
    },
  });

  return (
    <div className="space-y-4">
      <OrgTabs />
      <h1 className="hidden text-xl font-semibold md:block">SMTP Server</h1>
      <p className="-mt-2 text-sm text-slate-500">
        Legt fest, über welchen Mailserver dieser Verein E-Mails verschickt (z. B.
        Passwort-zurücksetzen-Links für Vereinsadmins). Ohne eigene Konfiguration gilt die
        globale Vorgabe der Plattform.
      </p>

      <Card title="Konfiguration">
        {isLoading ? (
          <p className="text-sm text-slate-400">Lädt…</p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="mail-mode"
                  checked={mode === 'global'}
                  disabled={readOnly}
                  onChange={() => setMode('global')}
                />
                Globale Konfiguration benutzen
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="mail-mode"
                  checked={mode === 'custom'}
                  disabled={readOnly}
                  onChange={() => setMode('custom')}
                />
                Eigene Konfiguration
              </label>
            </div>

            {mode === 'custom' && (
              <div className="rounded-md bg-slate-50 p-3">
                <MailConnectionFields
                  value={fields}
                  onChange={setFields}
                  hasPassword={data?.mail.custom.hasPassword ?? false}
                  hasApiKey={data?.mail.custom.hasApiKey ?? false}
                  hasClientSecret={data?.mail.custom.hasClientSecret ?? false}
                  disabled={readOnly}
                />
              </div>
            )}

            {!readOnly && (
              <div className="flex items-center gap-2">
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Speichert…' : 'Speichern'}
                </Button>
                {saved && <span className="text-sm text-green-600">Gespeichert ✓</span>}
              </div>
            )}
            {error && <Alert>{error}</Alert>}
          </form>
        )}
      </Card>

      <Card title="Testmail verschicken">
        <p className="mb-3 text-sm text-slate-500">
          Verschickt eine kurze Testmail über die aktuell wirksame Konfiguration dieses Vereins
          (eigene, falls oben ausgewählt, sonst die globale Vorgabe).
        </p>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setTestResult(null);
            sendTest.mutate();
          }}
        >
          <Field label="Empfänger">
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} required className="w-64" />
          </Field>
          <Button type="submit" disabled={!testTo.trim() || sendTest.isPending}>
            {sendTest.isPending ? 'Wird verschickt…' : 'Testmail verschicken'}
          </Button>
        </form>
        {testError && (
          <div className="mt-3">
            <Alert>{testError}</Alert>
          </div>
        )}
        {testResult && (
          <div className="mt-3">
            {testResult.sent ? (
              <Alert kind="success">
                Testmail verschickt (über {SOURCE_LABEL[testResult.source]}) — bitte Posteingang und Spam-Ordner
                prüfen.
              </Alert>
            ) : (
              <Alert>
                Fehlgeschlagen (über {SOURCE_LABEL[testResult.source]}): {testResult.error}
              </Alert>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
